/**
 * @fileoverview UserRateOverridesDialog — one person's individual rates
 * (MOR-1018).
 *
 * Rates are grade-based; this is the exception for someone charged off their
 * grade. Each override runs from a date to an optional end date and replaces
 * the grade rate while it is in force. A job's rate card still applies on top.
 * Admin/owner only — the queries behind it refuse everyone else.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { overrideInForce } from "@cvx/timesheets/rateMath";

import { extractErrorMessage } from "@/utils/errors";
import { Badge, Button, Label } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Spinner } from "@/ui/primitives/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { formatIsoDate, formatRate } from "./rateCards.helpers";

interface Draft {
  hourlyRate: string;
  effectiveFrom: string;
  effectiveTo: string;
  reason: string;
}

const EMPTY_DRAFT: Draft = {
  hourlyRate: "",
  effectiveFrom: "",
  effectiveTo: "",
  reason: "",
};

export function UserRateOverridesDialog({
  open,
  orgId,
  member,
  gradeLabel,
  today,
  onOpenChange,
}: {
  open: boolean;
  orgId: Id<"organizations">;
  member: { userId: Id<"users">; name: string } | null;
  /** "Partner, $650/hr" — what the person is charged at without an override. */
  gradeLabel: string;
  today: string;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        {/*
          The body is a child so the overrides query is only ever built with a
          real user id. `convexQuery` opens a Convex subscription as soon as its
          descriptor is in the query cache, whether or not `enabled` is set, so
          a placeholder id here had the server rejecting `""` on every
          reconnect. Keyed on the person so each open starts from a clean draft.
        */}
        {member && (
          <UserRateOverridesBody
            key={member.userId}
            orgId={orgId}
            member={member}
            gradeLabel={gradeLabel}
            today={today}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserRateOverridesBody({
  orgId,
  member,
  gradeLabel,
  today,
  onDone,
}: {
  orgId: Id<"organizations">;
  member: { userId: Id<"users">; name: string };
  gradeLabel: string;
  today: string;
  onDone: () => void;
}) {
  const { data: overrides } = useQuery(
    convexQuery(api.timesheets.rateOverrides.listForUser, {
      orgId,
      userId: member.userId,
    }),
  );
  const addOverride = useMutation(api.timesheets.rateOverrides.add);
  const endOverride = useMutation(api.timesheets.rateOverrides.end);
  const removeOverride = useMutation(api.timesheets.rateOverrides.remove);

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const hourlyRate = Number(draft.hourlyRate);
  const canAdd =
    draft.hourlyRate.trim() !== "" &&
    Number.isFinite(hourlyRate) &&
    hourlyRate >= 0 &&
    !!draft.effectiveFrom &&
    (!draft.effectiveTo || draft.effectiveTo >= draft.effectiveFrom);
  const inForce = overrides ? overrideInForce(overrides, today) : null;

  const handleAdd = async () => {
    if (!canAdd || saving) return;
    setSaving(true);
    try {
      await addOverride({
        orgId,
        userId: member.userId,
        hourlyRate,
        effectiveFrom: draft.effectiveFrom,
        effectiveTo: draft.effectiveTo || undefined,
        reason: draft.reason.trim() || undefined,
      });
      setDraft(EMPTY_DRAFT);
      toast.success("Individual rate added.");
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Could not add the individual rate."),
      );
    } finally {
      setSaving(false);
    }
  };

  const run = (action: Promise<unknown>, failure: string) =>
    action.catch((err: unknown) =>
      toast.error(extractErrorMessage(err, failure)),
    );

  return (
    <>
      <DialogHeader>
        <DialogTitle>Individual rate for {member.name}</DialogTitle>
        <DialogDescription>
          Without an individual rate this person is charged at their grade rate
          ({gradeLabel}). An individual rate replaces it between the dates you
          set. A job&apos;s rate card still applies on top.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {overrides === undefined ? (
          <div className="flex justify-center py-6">
            <Spinner className="text-muted-foreground" />
          </div>
        ) : overrides.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No individual rates. This person is on their grade rate.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border/50 rounded-md border border-border/60">
            {overrides.map((o) => (
              <li
                key={o._id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
              >
                <span className="w-24 font-medium tabular-nums">
                  {formatRate(o.hourlyRate)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatIsoDate(o.effectiveFrom)}{" "}
                  {o.effectiveTo
                    ? `to ${formatIsoDate(o.effectiveTo)}`
                    : "onwards"}
                </span>
                {inForce?._id === o._id && (
                  <Badge color="emerald" variant="soft">
                    In force
                  </Badge>
                )}
                <div className="ml-auto flex items-center gap-1">
                  {!o.effectiveTo && o.effectiveFrom <= today && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() =>
                        void run(
                          endOverride({
                            overrideId: o._id,
                            effectiveTo: today,
                          }),
                          "Could not end the individual rate.",
                        )
                      }
                    >
                      End today
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      void run(
                        removeOverride({ overrideId: o._id }),
                        "Could not delete the individual rate.",
                      )
                    }
                    aria-label={`Delete the ${formatRate(o.hourlyRate)} individual rate`}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
                {o.reason && (
                  <p className="w-full text-xs text-muted-foreground">
                    {o.reason}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-3 rounded-md bg-muted/30 p-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="override-rate">Hourly rate</Label>
              <Input
                id="override-rate"
                type="number"
                min={0}
                value={draft.hourlyRate}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    hourlyRate: e.target.value,
                  }))
                }
                placeholder="e.g. 480"
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label>From</Label>
              <DatePicker
                value={draft.effectiveFrom}
                onChange={(isoDate) =>
                  setDraft((prev) => ({
                    ...prev,
                    effectiveFrom: isoDate ?? "",
                  }))
                }
                placeholder="Start date"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Until (optional)</Label>
              <DatePicker
                value={draft.effectiveTo}
                onChange={(isoDate) =>
                  setDraft((prev) => ({
                    ...prev,
                    effectiveTo: isoDate ?? "",
                  }))
                }
                placeholder="Open-ended"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="override-reason">Reason (optional)</Label>
            <Input
              id="override-reason"
              value={draft.reason}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, reason: e.target.value }))
              }
              placeholder="e.g. Consultant agreement"
            />
          </div>
          {draft.effectiveTo && draft.effectiveTo < draft.effectiveFrom && (
            <p className="text-xs text-destructive">
              The end date is before the start date.
            </p>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={!canAdd || saving}
            onClick={() => void handleAdd()}
          >
            {saving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
            Add individual rate
          </Button>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Done
        </Button>
      </DialogFooter>
    </>
  );
}
